import React from "react";
import { SectieKop, Tegel } from "@/components/rust/Rust";
import { Link } from "react-router-dom";
import { OpvolgerAvatar } from "@/components/afspraken/Opvolger";
import { KPIS, kpiTone, randKleur, kortDatum, cap, callTekst, redenLabel, redenTone } from "./overzichtUtils";
import { Strook, StrookLegenda, terugkerendChip, roodRegel } from "./Strook";

export const KpiChips = ({ call, testid }) => (
  <span className="inline-flex gap-1" data-testid={testid}>
    {KPIS.map(([k, f]) => (
      <span key={k} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border ${kpiTone(call?.[f])}`}>{k}</span>
    ))}
  </span>
);

export const Metric = Tegel;   // v18-D2

export const SectieBalk = SectieKop;   // v18-D3: gedeelde kop

// Deel 1: de call die telt, met de week waar hij over gaat.
export const CallRegel = ({ r }) => {
  const c = r.laatsteCall;
  if (!c) return <span data-testid={`call-regel-${r.locatie}`} className="text-xs text-zinc-400">Nog geen call</span>;
  return (
    <span data-testid={`call-regel-${r.locatie}`} className={`inline-flex flex-wrap items-center gap-1.5 text-xs ${r.callDezeWeek ? "text-zinc-800" : "text-amber-800"}`}>
      <OpvolgerAvatar name={c.om} size="h-5 w-5 text-[10px]" />
      <span className="font-semibold">{callTekst(c)}</span>
      <span>· {cap(c.om)}{c.omInactief && " (uit dienst)"}</span>
      {r.callsInWeek > 1 && <span className="text-zinc-400">({r.callsInWeek} calls, de meest complete telt)</span>}
      {r.bezig && <span data-testid={`call-bezig-${r.locatie}`} className="font-semibold text-blue-700">· bezig {r.bezig.beantwoord}/{r.bezig.totaal}</span>}
    </span>
  );
};

// Deel 2: de lopende week — hoe ver de locatie is met de voorbereiding voor de volgende call.
export const VolgendeRegel = ({ r }) => {
  const v = r.voorbereidingVolgende;
  if (!v) return null;
  const tekst = v.status === "geen-account" ? "geen bedrijfsleider-account"
    : v.status === "nog-niet-open" ? `opent ${v.opentLabel}`
    : v.status === "open" ? `nog niets · open tot ${v.sluitLabel}`
    : v.status === "niets" ? "niets aangeleverd"
    : v.totaal > 0 ? `${v.ingevuld}/${v.totaal}` : "aangeleverd";
  const tone = v.status === "volledig" ? "text-emerald-700" : v.status === "onvolledig" ? "text-amber-800" : "text-zinc-500";
  return (
    <div data-testid={`deze-week-${r.locatie}`} className="text-xs text-zinc-500 border-t border-dashed border-zinc-200 pt-1.5">
      <span className="font-semibold text-zinc-700">Deze week</span> · voorbereiding voor {v.callLabel}: <span className={`font-mono font-semibold ${tone}`}>{tekst}</span>
      {r.weekritmeBl && <span className="font-mono"> · weekritme {r.weekritmeBl.gedaan}/{r.weekritmeBl.totaal}</span>}
    </div>
  );
};

// v17 D: informatie naast de stand (raakt status en tellers niet).
export const BezoekRegel = ({ r, link = false }) => {
  if (!r.laatsteBezoek) return <span data-testid={`laatst-bezocht-${r.locatie}`} className="text-xs text-zinc-400">Nog geen bezoek vastgelegd</span>;
  const tekst = `Laatst bezocht ${r.laatsteBezoek.label}${r.laatsteBezoek.omNaam ? ` · ${r.laatsteBezoek.omNaam}` : ""}`;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {link ? <Link data-testid={`laatst-bezocht-${r.locatie}`} to={`/bezoeken/${r.laatsteBezoek.id}`} onClick={(e) => e.stopPropagation()} className="text-xs text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline">{tekst}</Link>
        : <span data-testid={`laatst-bezocht-${r.locatie}`} className="text-xs text-zinc-500">{tekst}</span>}
      {r.bezoekOud && <span data-testid={`bezoek-oud-${r.locatie}`} className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5">28+ dagen niet bezocht</span>}
    </span>
  );
};

export const RedenChips = ({ r }) => (
  <span data-testid={`redenen-${r.locatie}`} className="flex flex-wrap gap-1">
    {(r.redenen || []).map((x) => (
      <span key={x.code} data-testid={`reden-${r.locatie}-${x.code}`} className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${redenTone(x.code)}`}>{redenLabel(x)}</span>
    ))}
  </span>
);

export const AfsprakenRegel = ({ r }) => (
  <span className="font-mono text-xs text-zinc-500">
    {r.open} open{r.teLaat > 0 && <>, <span className="text-rose-600 font-bold">{r.teLaat} te laat</span></>}
    {r.volgendeDeadline && `, volgende ${kortDatum(r.volgendeDeadline)}`}
  </span>
);

export const AandachtTegel = ({ r, onOpen }) => (
  <button type="button" data-testid={`tegel-${r.locatie}`} onClick={() => onOpen(r.locatie)}
    className={`text-left bg-white border border-zinc-200 border-l-[5px] ${randKleur(r)} rounded-[10px] p-3 space-y-2 hover:shadow-sm hover:-translate-y-px transition-[transform,box-shadow] duration-150`}>
    <div className="flex items-start justify-between gap-2">
      <span className="font-heading font-black text-zinc-900">{r.locatie}</span>
      <KpiChips call={r.laatsteCall} testid={`tegel-kpi-${r.locatie}`} />
    </div>
    <RedenChips r={r} />
    {r.terugkerend && <div className="flex flex-wrap items-center gap-2">{terugkerendChip(r)}<Strook r={r} testid={`tegel-strook-${r.locatie}`} /></div>}
    <div><CallRegel r={r} /></div>
    <div><AfsprakenRegel r={r} /></div>
    <p data-testid={`tegel-wat-${r.locatie}`} className={`text-xs line-clamp-2 ${r.watSpeeltEr ? "text-zinc-700" : "text-zinc-400 italic"}`}>{r.watSpeeltEr ? r.watSpeeltEr.tekst : "Nog niets vastgelegd"}</p>
    <div><BezoekRegel r={r} /></div>
    <VolgendeRegel r={r} />
  </button>
);

export const KleineTegel = ({ r, onOpen }) => (
  <button type="button" data-testid={`tegel-${r.locatie}`} onClick={() => onOpen(r.locatie)}
    className={`text-left bg-white border border-zinc-200 border-l-[5px] ${randKleur(r)} rounded-[10px] px-3 py-2 space-y-1.5 hover:shadow-sm transition-shadow duration-150`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-heading font-black text-sm text-zinc-900 truncate">{r.locatie}</span>
      <span className="font-mono text-xs text-zinc-500 shrink-0">{r.open} open</span>
    </div>
    <div className="flex flex-wrap items-center gap-2"><CallRegel r={r} />{r.laatsteCall && <KpiChips call={r.laatsteCall} />}</div>
    <div><BezoekRegel r={r} /></div>
    <VolgendeRegel r={r} />
  </button>
);

const TH = ({ children }) => <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">{children}</th>;

export const StandTabel = ({ rows, onOpen }) => (
  <div data-testid="overzicht-tabel" className="overflow-x-auto rounded-[10px] border border-zinc-200 bg-white">
    <table className="w-full min-w-[1520px] text-sm">
      <thead className="bg-zinc-950 text-white">
        <tr><TH>Locatie</TH><TH>Status</TH><TH>13 weken</TH><TH>Call die telt</TH><TH>O · B · P · WOW</TH><TH>Open</TH><TH>Te laat</TH><TH>Volgende</TH><TH>Wat speelt er</TH><TH>Laatst bezocht</TH><TH>Deze week</TH></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.locatie} data-testid={`tabel-rij-${r.locatie}`} onClick={() => onOpen(r.locatie)}
            className={`cursor-pointer border-l-4 ${randKleur(r)} odd:bg-white even:bg-zinc-50 hover:bg-blue-50 transition-colors align-top`}>
            <td className="px-3 py-2 font-semibold text-zinc-900 whitespace-nowrap">{r.locatie}</td>
            <td className="px-3 py-2 min-w-[150px]">{(r.redenen || []).length ? <RedenChips r={r} /> : <span className={`text-xs font-semibold ${r.status === "geen-call" ? "text-zinc-500" : "text-emerald-700"}`}>{r.status === "geen-call" ? "Nog geen call" : "Op koers"}</span>}</td>
            <td className="px-3 py-2 space-y-1"><Strook r={r} testid={`tabel-strook-${r.locatie}`} />{r.terugkerend && terugkerendChip(r)}</td>
            <td className="px-3 py-2"><CallRegel r={r} /></td>
            <td className="px-3 py-2">{r.laatsteCall ? <KpiChips call={r.laatsteCall} /> : <span className="text-zinc-400">–</span>}</td>
            <td className="px-3 py-2 font-mono">{r.open}</td>
            <td className={`px-3 py-2 font-mono ${r.teLaat > 0 ? "text-rose-600 font-bold" : ""}`}>{r.teLaat}</td>
            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{kortDatum(r.volgendeDeadline) || "–"}</td>
            <td className={`px-3 py-2 text-xs max-w-[220px] truncate ${r.watSpeeltEr ? "text-zinc-700" : "text-zinc-400 italic"}`}>{r.watSpeeltEr ? r.watSpeeltEr.tekst : "Nog niets vastgelegd"}</td>
            <td className="px-3 py-2 min-w-[170px]"><BezoekRegel r={r} link /></td>
            <td className="px-3 py-2 min-w-[220px]"><VolgendeRegel r={r} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PerOmKaart = ({ o }) => (
  <div data-testid={`nu-om-${o.om}`} className="bg-white border border-zinc-200 rounded-[10px] p-3 flex items-center gap-3">
    <OpvolgerAvatar name={o.om} size="h-9 w-9 text-sm" />
    <div className="min-w-0">
      <div className="font-heading font-black text-zinc-900 capitalize">{o.om}</div>
      <div className="font-mono text-xs text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5">
        <span data-testid={`nu-om-gebeld-${o.om}`}>gebeld {o.gebeld ?? 0}/{o.toegewezen ?? 0}</span>
        <span data-testid={`nu-om-cijfers-${o.om}`}>klaar {o.cijfersCompleet ?? 0}/{o.gebeld ?? 0}</span>
        <span data-testid={`nu-om-telaat-${o.om}`}>te laat als opvolger {o.teLaatAlsOpvolger ?? 0}</span>
        <span>weekritme {o.weekritmeTotal == null ? "–" : `${o.weekritmeDone}/${o.weekritmeTotal}`}</span>
      </div>
    </div>
  </div>
);

// v10-C2: terugkerend rood — over de locatie, nooit over de OM.
export function TerugkerendSectie({ rows, onOpen, vanafLabel }) {
  const lijst = rows.filter((r) => r.terugkerend).sort((a, b) => b.roodAantal - a.roodAantal || a.locatie.localeCompare(b.locatie));
  return (
    <div id="terugkerend" data-testid="sectie-terugkerend-blok" className="mb-3 md:mb-4">
      <SectieBalk testid="sectie-terugkerend" titel="Terugkerend rood" aantal={lijst.length} hint="twee weken op rij, of vier van de dertien" accent />
      {lijst.length === 0 ? <p className="text-sm text-zinc-500 mb-2">Geen locatie met terugkerend rood.</p> : (
        <div className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200 mb-2">
          {lijst.map((r) => (
            <button key={r.locatie} type="button" data-testid={`terugkerend-${r.locatie}`} onClick={() => onOpen(r.locatie)}
              className="w-full text-left flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2.5 hover:bg-zinc-50 transition-colors">
              <span className="font-heading font-black text-zinc-900 min-w-[120px]">{r.locatie}</span>
              <span className="flex -space-x-1">{(r.oms || []).map((o) => <OpvolgerAvatar key={o} name={o} size="h-5 w-5 text-[10px]" />)}</span>
              <Strook r={r} testid={`terugkerend-strook-${r.locatie}`} />
              <span data-testid={`terugkerend-regel-${r.locatie}`} className="font-mono text-xs text-rose-700 font-semibold">{roodRegel(r)}</span>
              <span data-testid={`terugkerend-open-${r.locatie}`} className={`font-mono text-xs ${r.open === 0 ? "text-rose-600 font-bold" : "text-zinc-500"}`}>{r.open} open {r.open === 1 ? "afspraak" : "afspraken"}</span>
            </button>
          ))}
        </div>
      )}
      <StrookLegenda reeks={rows[0]?.reeks} vanafLabel={vanafLabel} />
    </div>
  );
}
